import React, { useState } from 'react';
import { Btn, DangerBtn, Loading, Msg, panelPost, useAdminGet } from '../kit';
import { Labeled, Modal, inputCls } from '../email/emailKit';
import { LIFECYCLE_LABEL, toLifecycle } from '../tags/shared';

/**
 * Change plan — one account, opened from the user drawer (Plan section) and
 * from Billing → Users. Replaces the old prompt-chain "Downgrade" button.
 *
 * GET users/plan says where the account is now; POST users/change_plan does
 * the move (billing_service.admin_change_plan). Three kinds of move:
 *   free  — to Explorer: now, or at the end of the paid period / trial;
 *   move  — a paid plan to another paid plan, keeping the same end date;
 *   grant — Explorer or a trial to a paid plan, for N months.
 * Whichever it is, any live Razorpay mandate is stopped. A stop Razorpay did
 * not confirm is shown loudly: it has to be cancelled by hand, or the next
 * renewal charge would bring the old plan back.
 */

type Plan = { code: string; name: string; price_inr_paise: number; free: boolean };
type Live = {
  plan_code: string; plan_name?: string; status: string; is_trial: boolean; source?: string | null;
  auto_renew?: boolean; current_start?: string | null; current_end?: string | null; has_mandate?: boolean;
};
export type PlanInfo = { user_id: string; state: string; trial: any; live: Live | null; plans: Plan[] };
type Kind = 'free' | 'move' | 'grant';
type Flash = { kind: 'error' | 'ok'; text: string };

const fmtDay = (v: any): string => {
  if (v === null || v === undefined || v === '') return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const inr = (paise: number) => (paise > 0 ? `₹${(paise / 100).toLocaleString('en-IN')}` : 'free');

/** Same month arithmetic as the server's grant (calendar months, day clamped). */
const addMonths = (d: Date, n: number) => {
  const out = new Date(d);
  const day = out.getDate();
  out.setDate(1);
  out.setMonth(out.getMonth() + n);
  out.setDate(Math.min(day, new Date(out.getFullYear(), out.getMonth() + 1, 0).getDate()));
  return out;
};

/** How the live row came to exist (admin_user360 uses the same sources). */
const SOURCE_WORD: Record<string, string> = {
  razorpay_subscription: 'paying through Razorpay',
  razorpay_order: 'paid once',
  manual: 'given by the team',
  trial_admin: 'given by the team',
  trial_mandate: 'autopay trial',
};

/** Contact-limit tiers (Backend/contact_limits.classify) in admin words. */
const TIER_WORD: Record<string, string> = {
  trial: 'trial', paid: 'paid-plan', free: 'Explorer (free)', admin: 'team',
};

const alertsChanged = () => {
  try { document.dispatchEvent(new Event('utech:alerts-changed')); } catch { /* not in a browser */ }
};

/** Where the account is now: a headline ("Voyager — free trial") and the
 *  facts behind it (end date, renewal, mandate). Shared with PlanSection. */
export const planLines = (d: PlanInfo | null | undefined): { head: string; detail: string; lifecycle: string } => {
  if (!d) return { head: '', detail: '', lifecycle: '' };
  const lifecycle = d.state ? (LIFECYCLE_LABEL[toLifecycle(d.state)] || String(d.state).replace(/_/g, ' ')) : '';
  const l = d.live;
  if (!l) {
    const ended = d.trial?.ends_at ? `trial ended ${fmtDay(d.trial.ends_at)}` : '';
    return { head: 'Explorer (free)', detail: ['no live plan', ended].filter(Boolean).join(' · '), lifecycle };
  }
  const name = l.plan_name || l.plan_code;
  const left = l.is_trial && d.trial?.days_left !== undefined && d.trial?.days_left !== null
    ? ` (${d.trial.days_left} day${Number(d.trial.days_left) === 1 ? '' : 's'} left)` : '';
  const renew = l.status === 'cancelled' ? 'cancelled — runs to its end date, no renewal'
    : l.is_trial ? '' : l.auto_renew ? 'renews automatically' : 'does not renew';
  const detail = [
    SOURCE_WORD[l.source || ''] || '',
    l.current_end ? `${l.is_trial ? 'trial ends' : 'access until'} ${fmtDay(l.current_end)}${left}` : 'no end date',
    renew,
    `Razorpay mandate: ${!l.has_mandate ? 'no' : l.status === 'cancelled' ? 'stopped' : 'yes'}`,
  ].filter(Boolean).join(' · ');
  return { head: l.is_trial ? `${name} — free trial` : name, detail, lifecycle };
};

/** "Moved to Explorer — paid access ended now", from a change_plan answer. */
const changeHeadline = (res: any): string => {
  const r = res?.result || {};
  const names: Record<string, string> = {};
  (res?.plan?.plans || []).forEach((p: Plan) => { names[p.code] = p.name; });
  const to = names[r.to_plan] || r.to_plan;
  return r.kind === 'free'
    ? (r.effective === 'now' ? `Moved to ${to} — ${r.from_trial ? 'trial' : 'paid access'} ended now`
      : `Moving to ${to} — keeps what they had until ${fmtDay(r.access_until)}`)
    : r.kind === 'grant' ? `Gave ${to} until ${fmtDay(r.access_until)}`
      : `Moved to ${to} — access until ${fmtDay(r.access_until)}`;
};

/** One line for the host's flash after the dialog closes. */
export const describePlanChange = (res: any): Flash => {
  const r = res?.result;
  if (!r) return { kind: 'error', text: 'The plan change did not report back — refresh to check.' };
  const head = changeHeadline(res);
  const failed: string[] = r.gateway?.failed || [];
  const mail = r.notified ? 'e-mail sent' : r.notify_error ? `e-mail NOT sent (${r.notify_error})` : 'no e-mail';
  if (failed.length) {
    return { kind: 'error', text: `${head}. Razorpay did not confirm the stop for ${failed.join(', ')} — cancel manually in Razorpay. ${mail}.` };
  }
  return { kind: r.notify_error ? 'error' : 'ok', text: `${head}. ${mail}.` };
};

/** A checkbox with a line under it saying what ticking it means. */
const Tick: React.FC<{
  checked: boolean; onChange: (v: boolean) => void; label: React.ReactNode; hint?: React.ReactNode;
}> = ({ checked, onChange, label, hint }) => (
  <label className="flex items-start gap-2 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
      className="mt-1 accent-primary-500" />
    <span className="flex flex-col gap-0.5 min-w-0">
      <span className="text-sm text-grayscale-1300">{label}</span>
      {hint && <span className="text-xs text-grayscale-1100 break-words">{hint}</span>}
    </span>
  </label>
);

const Radio: React.FC<{
  checked: boolean; onChange: () => void; label: React.ReactNode; hint?: React.ReactNode;
}> = ({ checked, onChange, label, hint }) => (
  <label className="flex items-start gap-2 cursor-pointer">
    <input type="radio" checked={checked} onChange={onChange} className="mt-1 accent-primary-500" />
    <span className="flex flex-col gap-0.5">
      <span className="text-sm text-grayscale-1300">{label}</span>
      {hint && <span className="text-xs text-grayscale-1100">{hint}</span>}
    </span>
  </label>
);

export const ChangePlanModal: React.FC<{
  userId: string; email?: string; onClose: () => void; onDone: (res: any) => void;
}> = ({ userId, email, onClose, onDone }) => {
  const info = useAdminGet<PlanInfo>('users', 'plan', { user_id: userId }, [userId]);
  // Only for the consequences list (which contact limit applies after the
  // move, and whether their own limit still counts); the dialog works without.
  const cl = useAdminGet('users', 'contact_limit', { user_id: userId }, [userId]);
  const [to, setTo] = useState('');
  // Keeping the time they have is the default; taking it away is the
  // deliberate answer, as it was in the old Downgrade.
  const [effective, setEffective] = useState<'now' | 'period_end'>('period_end');
  const [months, setMonths] = useState('12');
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState<any>(null);

  const d = info.data;
  const live = d?.live || null;
  const plans: Plan[] = d?.plans || [];
  const fromTrial = !!live?.is_trial;
  const fromPlan = plans.find(p => p.code === live?.plan_code);
  // A retired plan is not in the list; it is paid unless it is Explorer.
  const fromPaid = !!live && !fromTrial && !(fromPlan ? fromPlan.free : live.plan_code === 'explorer');
  const fromName = live ? (live.plan_name || live.plan_code) : 'Explorer';
  const fromLabel = fromTrial ? `${fromName} trial` : fromName;
  const target = plans.find(p => p.code === to) || null;
  const kind: Kind | null = !target ? null : target.free ? 'free' : fromPaid ? 'move' : 'grant';
  const monthsN = Math.floor(Number(months));
  const monthsOk = String(months).trim() !== '' && Number.isFinite(monthsN) && monthsN >= 1 && monthsN <= 120;
  const end = live?.current_end || null;
  const grantEnd = monthsOk ? addMonths(new Date(), monthsN) : null;
  const untilText = kind === 'free' ? (effective === 'now' ? 'today' : (end ? fmtDay(end) : 'no end date'))
    : kind === 'move' ? fmtDay(end) : kind === 'grant' ? fmtDay(grantEnd) : '';

  // Why a plan cannot be picked (shown in the option, and the server refuses it too).
  const blocked = (p: Plan) =>
    p.free && !live ? 'already on it'
      : fromPaid && p.code === live?.plan_code ? 'current plan' : '';

  const why = !d ? 'Loading…'
    : !target ? 'Choose the plan to move them to'
      : blocked(target) ? `They are already on ${target.name}`
        : kind === 'grant' && !monthsOk ? 'Months must be 1 to 120'
          : '';
  const fromPrice = fromPlan ? fromPlan.price_inr_paise : Infinity;
  // Anything that takes access away gets the two-click confirm.
  const losing = kind === 'free' || (kind === 'move' && !!target && target.price_inr_paise < fromPrice);

  const preview = !kind || !target ? ''
    : kind === 'grant' ? `“Welcome to ${target.name}” (the welcome paying members get, without the payment lines): plan valid until ${untilText}.`
      : kind === 'free' && effective === 'now'
        ? `“Plan changed”: ${fromLabel} → ${target.name}. This takes effect today.`
        : kind === 'free' ? `“Plan changed”: they keep ${fromLabel} until ${untilText}, then move to ${target.name}.`
          : `“Plan changed”: ${fromLabel} → ${target.name} from today; access runs until ${untilText}.`;

  // ---- What will happen ------------------------------------------------
  const consequences: React.ReactNode[] = [];
  if (kind && target) {
    if (kind === 'free' && effective === 'now') {
      consequences.push(`${fromTrial ? 'Their trial' : 'Paid access'} ends today. They keep their account, saved items and history, as an Explorer.`);
    } else if (kind === 'free') {
      consequences.push(end
        ? `They keep ${fromLabel} until ${fmtDay(end)}, then move to ${target.name}. It will not renew.`
        : `${fromLabel} has no end date on record, so it keeps running (without renewing) until someone ends it now.`);
    } else if (kind === 'move') {
      consequences.push(`${target.name} from today, ending ${fmtDay(end)} (their current end date). Set by the team — it does not renew.`);
    } else {
      consequences.push(`${target.name} from today until ${untilText}${monthsOk ? ` (${monthsN} month${monthsN === 1 ? '' : 's'})` : ''}`
        + `${fromTrial ? ', replacing their trial' : ''}. Given by the team — nothing is charged and it does not renew.`);
    }
    consequences.push(live?.has_mandate
      ? 'Their Razorpay renewal is stopped now, so nothing more is charged.'
      : 'Any live Razorpay renewal is stopped now (their current plan has none), so nothing more is charged.');

    const u = cl.data?.usage;
    const nowTier: string = u?.tier || (fromTrial ? 'trial' : fromPaid ? 'paid' : 'free');
    const nextTier = kind === 'free' ? 'free' : 'paid';
    const when = kind === 'free' && effective === 'period_end' && end ? ` from ${fmtDay(end)}` : '';
    if (nowTier !== 'admin') {
      consequences.push(nowTier === nextTier
        ? `University-contact limit: stays on the ${TIER_WORD[nowTier] || nowTier} limit.`
        : `University-contact limit: moves from the ${TIER_WORD[nowTier] || nowTier} limit to the `
          + `${TIER_WORD[nextTier]} limit${when}.`);
      const ov = u?.override;
      if (ov) {
        const lim = ov.limit === null || ov.limit === undefined ? 'unlimited' : Number(ov.limit).toLocaleString();
        consequences.push(ov.tier === nextTier
          ? `Their own contact limit (${lim}) was set for the ${TIER_WORD[ov.tier] || ov.tier} plan, so it applies on the new plan${when}.`
          : `Their own contact limit (${lim}) only applies to the ${TIER_WORD[ov.tier] || ov.tier} plan it was set for — `
            + `on the new plan${when} the ${TIER_WORD[nextTier]} default counts instead.`);
      } else if (cl.error) {
        consequences.push("An account's own contact limit only applies to the plan it was set for.");
      }
    }
  }

  const submit = async () => {
    if (why || busy || !target || !kind) return;
    setBusy(true);
    setErr('');
    const body: Record<string, any> = {
      user_id: userId, to_plan_code: target.code, notify,
      effective: kind === 'free' ? effective : 'now', note: note.trim().slice(0, 500),
    };
    if (kind === 'grant') body.months = monthsN;
    const { data, error } = await panelPost('users', 'change_plan', body);
    setBusy(false);
    if (error || !data?.result) { setErr(error || 'The change failed.'); return; }
    setDone(data);
    // A Razorpay stop that failed is raised as an alert; let the bell re-read.
    alertsChanged();
  };

  // Once it has changed, every way out reports back so the host reloads.
  const close = () => { if (busy) return; if (done) onDone(done); else onClose(); };

  const confirmLabel = !kind || !target ? 'Change plan'
    : kind === 'free' ? (effective === 'now' ? 'End access now' : `Move to ${target.name} at period end`)
      : kind === 'move' ? `Change to ${target.name}`
        : `Give ${target.name}${monthsOk ? ` for ${monthsN} month${monthsN === 1 ? '' : 's'}` : ''}`;

  // ---- Result -----------------------------------------------------------
  if (done) {
    const r = done.result || {};
    const failed: string[] = r.gateway?.failed || [];
    const cancelled: string[] = r.gateway?.cancelled || [];
    return (
      <Modal title="Plan changed" onClose={close}
        footer={<Btn kind="primary" onClick={close}>Done</Btn>}>
        <div className="flex flex-col gap-3 max-h-[65vh] overflow-y-auto pr-1">
          {failed.length > 0 && (
            <div className="rounded-lg border-2 border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 flex flex-col gap-1">
              <b>Cancel manually in Razorpay.</b>
              <span>
                Razorpay did not confirm the stop for <span className="font-mono break-all">{failed.join(', ')}</span>.
                Until it is cancelled there, the next renewal could charge them and bring the old plan back.
                The team has been alerted.
              </span>
            </div>
          )}
          <Msg kind="ok">{changeHeadline(done)}.</Msg>
          <ul className="list-disc ml-4 flex flex-col gap-1 text-xs text-grayscale-1200">
            <li>
              {r.kind === 'free' && r.effective === 'now'
                ? `Their ${r.from_trial ? 'trial' : 'paid access'} ended now.`
                : `${r.kind === 'free' ? 'Keeps current access' : 'Access'} until ${fmtDay(r.access_until)}.`}
            </li>
            <li>
              {cancelled.length ? `Stopped ${cancelled.length} Razorpay renewal${cancelled.length === 1 ? '' : 's'}.`
                : failed.length ? 'No Razorpay renewal was confirmed stopped.'
                  : 'No Razorpay renewal to stop.'}
            </li>
            <li className={r.notify_error ? 'text-amber-700' : ''}>
              {r.notified ? 'E-mail sent to the user.'
                : r.notify_error ? `E-mail NOT sent: ${r.notify_error}`
                  : 'No e-mail sent.'}
            </li>
          </ul>
        </div>
      </Modal>
    );
  }

  // ---- Form -------------------------------------------------------------
  const lines = planLines(d);
  return (
    <Modal title="Change plan" onClose={close}
      footer={
        <>
          <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
          {busy ? <Btn kind={losing ? 'danger' : 'primary'} disabled>Changing…</Btn>
            : why ? <Btn kind={losing ? 'danger' : 'primary'} disabled title={why}>{confirmLabel}</Btn>
              : losing ? <DangerBtn onConfirm={submit}>{confirmLabel}</DangerBtn>
                : <Btn kind="primary" onClick={submit}>{confirmLabel}</Btn>}
        </>
      }>
      <div className="flex flex-col gap-3 max-h-[65vh] overflow-y-auto pr-1">
        {info.error && <Msg kind="error">{info.error}</Msg>}
        {info.loading && !d ? <Loading /> : d && (
          <>
            <div className="rounded-lg bg-grayscale-200 px-3 py-2 text-xs text-grayscale-1100 flex flex-col gap-0.5">
              {email && <div className="text-grayscale-1300 font-medium break-all">{email}</div>}
              <div className="text-sm text-grayscale-1300">Now: <b>{lines.head}</b></div>
              {lines.detail && <div>{lines.detail}</div>}
              {lines.lifecycle && <div>Lifecycle: {lines.lifecycle}</div>}
            </div>

            <Labeled label="Move to">
              <select value={to} onChange={e => { setTo(e.target.value); setErr(''); }} className={inputCls}>
                <option value="">Choose a plan…</option>
                {plans.map(p => {
                  const b = blocked(p);
                  return (
                    <option key={p.code} value={p.code} disabled={!!b}>
                      {p.name} — {inr(p.price_inr_paise)}{b ? ` (${b})` : ''}
                    </option>
                  );
                })}
              </select>
            </Labeled>

            {kind === 'free' && (
              <div className="flex flex-col gap-1.5">
                <div className="text-xs text-grayscale-1100">When</div>
                <Radio checked={effective === 'period_end'} onChange={() => setEffective('period_end')}
                  label={fromTrial ? `When their trial ends (${fmtDay(end)})`
                    : `At the end of their ${live?.source === 'manual' ? 'current' : 'paid'} period (${fmtDay(end)})`}
                  hint="They keep what they have until then; it does not renew." />
                <Radio checked={effective === 'now'} onChange={() => setEffective('now')}
                  label={<span className="text-red-700 font-medium">End access now</span>}
                  hint={`They lose ${fromLabel} today${fromPaid ? ' — no refund is made here (use Refund in Billing → Users)' : ''}.`} />
              </div>
            )}

            {kind === 'grant' && (
              <Labeled label="Months" hint={monthsOk ? `access until ${fmtDay(grantEnd)}` : '1 to 120'}>
                <input type="number" min={1} max={120} step={1} value={months}
                  onChange={e => setMonths(e.target.value)}
                  className="h-9 w-28 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300" />
              </Labeled>
            )}

            {kind === 'move' && (
              <div className="text-xs text-grayscale-1100 -mt-1">
                Keeps their current end date ({fmtDay(end)}); the price difference is not charged or refunded.
              </div>
            )}

            <Labeled label="Note" hint={`optional · ${note.length}/500 · kept on their billing record, not e-mailed`}>
              <input value={note} maxLength={500} onChange={e => setNote(e.target.value)} className={inputCls} />
            </Labeled>

            <Tick checked={notify} onChange={setNotify} label="Notify the user by e-mail"
              hint={!kind ? 'Pick a plan to see what they will be told.'
                : notify ? preview : 'No e-mail — they will see the change next time they sign in.'} />

            {consequences.length > 0 && (
              <div className={`rounded-lg border px-3 py-2 text-xs flex flex-col gap-1 ${losing
                ? 'border-amber-200 bg-amber-50 text-amber-800'
                : 'border-grayscale-200 bg-grayscale-100 text-grayscale-1200'}`}>
                <b className="text-sm">What happens</b>
                <ul className="list-disc ml-4 flex flex-col gap-0.5">
                  {consequences.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            )}
          </>
        )}
        {err && <Msg kind="error">{err}</Msg>}
      </div>
    </Modal>
  );
};

export default ChangePlanModal;
